import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Utensils, ShoppingBag, Music, Hotel, PartyPopper, Car, MapPin, ShieldCheck, Clock, ArrowRight, Phone, MessageSquare, Plane, Tag, FileText, Home } from "lucide-react";
import { useSEO } from "@/lib/seo";

const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_luxury-transport-hub-5/artifacts/ubfcqy49_shutterstock_1841153455-2%20-%20Copy.webp";
const PHONE = "+19703156500";
const CANONICAL = "https://www.aspenexecutivelimos.com/aspen-area-transportation";

const HIGHLIGHTS = [
  { icon: MapPin, title: "Local Chauffeurs", desc: "Knowledgeable, professional local drivers who know every restaurant, venue, and neighborhood." },
  { icon: Car, title: "Luxury 4WD SUVs", desc: "Immaculate four-wheel-drive SUVs built for comfort and Colorado's mountain roads." },
  { icon: ShieldCheck, title: "Door-to-Door Service", desc: "Curbside pickup and drop-off — no parking, no driving, no waiting." },
  { icon: Clock, title: "On Your Schedule", desc: "Personalized, punctual service built entirely around your plans, in any weather." },
];

const SERVICES = [
  { icon: Utensils, title: "Dining & Nightlife", desc: "Transportation to restaurants, lounges, private clubs, and evening destinations — without the need to park or drive." },
  { icon: ShoppingBag, title: "Shopping & Downtown Aspen", desc: "Transportation for downtown Aspen, Snowmass Village, Willits, and other local shopping destinations." },
  { icon: Music, title: "Concerts & Entertainment", desc: "Transportation to concerts, festivals, performances, and local entertainment venues." },
  { icon: Hotel, title: "Hotels & Resorts", desc: "Transportation between hotels, resorts, restaurants, airports, and local attractions." },
  { icon: PartyPopper, title: "Special Events", desc: "Private transportation for galas, corporate dinners, festivals, celebrations, and other scheduled events." },
];

const AREAS = ["Aspen", "Snowmass", "Basalt", "El Jebel", "Willits", "Carbondale", "Glenwood Springs", "The Roaring Fork Valley"];

const FAQS = [
  {
    q: "Do you offer evening and late-night pickups?",
    a: "Yes. Aspen Executive Limo provides scheduled evening and late-night transportation for dinners, concerts, entertainment, and special events. Advance reservations are recommended.",
  },
  {
    q: "Can I book round-trip transportation to dinner?",
    a: "Yes. Guests may reserve round-trip transportation or arrange hourly chauffeur service, depending on their plans and schedule.",
    links: [{ to: "/#services", label: "Hourly Hire", testid: "aspen-faq-link-hourly" }],
  },
  {
    q: "Which Aspen-area communities do you serve?",
    a: "We provide transportation throughout Aspen, Snowmass, Basalt, El Jebel, Willits, Carbondale, Glenwood Springs, and the Roaring Fork Valley.",
  },
  {
    q: "How do I request a quote?",
    a: "Use the Request a Quote form, or call or text us directly for personalized pricing and availability.",
    links: [
      { to: "/reserve", label: "Request a Quote", testid: "aspen-faq-link-quote" },
      { to: "/rates", label: "View our Rates", testid: "aspen-faq-link-rates" },
    ],
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const CTA_BTN = "inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold text-base tracking-wide rounded-sm px-[3px] py-[1px] shadow-xl hover:bg-[#E0BC4A] transition-colors";

const RELATED = [
  { icon: Plane, title: "Airport Transportation", desc: "Seamless transportation to and from Aspen, Denver, Eagle, Rifle, and Grand Junction airports.", to: "/airport-transportation", testid: "related-airport" },
  { icon: Car, title: "Fleet", desc: "Explore our luxury four-wheel-drive SUVs built for Colorado mountain travel.", to: "/fleet", testid: "related-fleet" },
  { icon: Tag, title: "Rates", desc: "View flat-rate pricing for our most popular destinations.", to: "/rates", testid: "related-rates" },
  { icon: Clock, title: "Hourly Hire", desc: "Reserve a chauffeur by the hour for flexible transportation.", to: "/#services", testid: "related-hourly" },
  { icon: FileText, title: "Request a Quote", desc: "Receive a personalized quote for your itinerary.", to: "/reserve", testid: "related-quote" },
  { icon: Home, title: "Home", desc: "Return to the Aspen Executive Limo homepage to explore everything we offer.", to: "/", testid: "related-home" },
];

const SEO_DESC = "Private luxury SUV transportation throughout Aspen and the Roaring Fork Valley — dining, shopping, concerts, hotels, and special events. Aspen Executive Limo's local chauffeurs offer door-to-door, all-weather service on your schedule.";

const BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://www.aspenexecutivelimos.com/#business",
  name: "Aspen Executive Limo",
  description: SEO_DESC,
  url: CANONICAL,
  image: HERO_IMG,
  telephone: "+1-970-315-6500",
  priceRange: "$$$",
  address: { "@type": "PostalAddress", addressLocality: "Aspen", addressRegion: "CO", addressCountry: "US" },
  areaServed: [
    { "@type": "City", name: "Aspen, CO" },
    { "@type": "City", name: "Snowmass, CO" },
    { "@type": "City", name: "Basalt, CO" },
    { "@type": "City", name: "El Jebel, CO" },
    { "@type": "City", name: "Carbondale, CO" },
    { "@type": "City", name: "Glenwood Springs, CO" },
  ],
  makesOffer: {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name: "Aspen Area Transportation",
      serviceType: "Private chauffeured luxury SUV transportation within Aspen and the Roaring Fork Valley",
      areaServed: "Aspen and the Roaring Fork Valley, Colorado",
    },
  },
};

const BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aspenexecutivelimos.com/" },
    { "@type": "ListItem", position: 2, name: "Aspen Area Transportation", item: CANONICAL },
  ],
};

export default function AspenAreaTransportation() {
  useSEO({
    title: "Aspen Area Transportation | Private Luxury Car Service — Aspen Executive Limo",
    description: SEO_DESC,
    keywords: "Aspen local transportation, Aspen private car service, Aspen dinner transportation, Aspen event transportation, Roaring Fork Valley transportation, Aspen luxury SUV service",
    canonical: CANONICAL,
    image: HERO_IMG,
    jsonLd: [BUSINESS_LD, BREADCRUMB_LD, FAQ_LD],
  });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" data-testid="aspen-area-transportation-page">
      <Navbar />

      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img src={HERO_IMG} alt="Downtown Aspen and the Roaring Fork Valley" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-[120px] pb-24 sm:pt-[160px] sm:pb-32">
          <p className="text-xs tracking-label uppercase font-bold text-[#D4AF37] mb-4" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>Aspen Executive Limo</p>
          <h1 className="font-heading text-5xl sm:text-7xl font-light leading-[1.05] tracking-tight text-white max-w-3xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>
            Aspen Area Transportation
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-zinc-100 font-light leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.85)" }}>
            Private luxury transportation for dining, shopping, concerts, special events, hotels, and destinations throughout Aspen and the Roaring Fork Valley.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reserve" data-testid="aspen-reserve-btn" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`sms:${PHONE}`} data-testid="aspen-calltext-btn" className={CTA_BTN}><MessageSquare size={16} /> Call or Text Now</a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Effortless Travel, Anywhere in the Valley</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
          Aspen Executive Limo provides comfortable, punctual, private transportation for guests traveling between hotels, restaurants, entertainment venues, shopping districts, local events, and nearby communities. Our knowledgeable local chauffeurs and luxury four-wheel-drive SUVs deliver safe, all-weather, door-to-door curbside service — personalized entirely around your schedule, so you simply relax and enjoy the ride.
        </p>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HIGHLIGHTS.map((f) => (
              <div key={f.title} data-testid={`aspen-highlight-${f.title}`} className="group bg-[#101012] border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
                <f.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
                <h3 className="font-heading text-xl mb-2 text-white">{f.title}</h3>
                <p className="text-zinc-400 font-light text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Where We Take You</h2>
        <p className="mt-4 text-center text-zinc-400 font-light">Private transportation for every occasion across Aspen and the Valley.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} data-testid={`aspen-service-${s.title}`} className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500">
              <s.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white">{s.title}</h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Serving the Entire Roaring Fork Valley</h2>
          <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
          <p className="mt-6 text-lg text-zinc-300 font-light leading-relaxed">
            We provide private luxury transportation throughout Aspen, Snowmass, Basalt, El Jebel, Willits, Carbondale, Glenwood Springs, and the greater Roaring Fork Valley.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {AREAS.map((a) => (
              <span key={a} data-testid={`aspen-area-${a}`} className="inline-flex items-center gap-2 bg-[#101012] border border-white/10 rounded-sm px-4 py-2 text-sm text-zinc-200">
                <MapPin size={14} className="gold-text" /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-20" data-testid="aspen-faq">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Frequently Asked Questions</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`aspen-faq-item-${i}`} className="bg-[#101012] border border-white/10 rounded-sm p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-heading text-xl text-white">{f.q}</h3>
              <p className="mt-3 text-zinc-400 font-light text-sm leading-relaxed">{f.a}</p>
              {f.links && (
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
                  {f.links.map((l) => (
                    <Link key={l.to} to={l.to} data-testid={l.testid} className="inline-flex items-center gap-1.5 gold-text text-sm font-medium hover:gap-2.5 transition-all">
                      {l.label} <ArrowRight size={14} />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="related-services">
        <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white text-center">Continue Planning Your Trip</h2>
        <div className="mt-4 h-px w-16 bg-[#D4AF37]/60 mx-auto" />
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RELATED.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              data-testid={r.testid}
              className="group bg-black/40 backdrop-blur-md border border-white/10 rounded-sm p-8 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-500"
            >
              <r.icon size={26} strokeWidth={1.5} className="gold-text mb-5" />
              <h3 className="font-heading text-xl mb-2 text-white flex items-center gap-2">
                {r.title}
                <ArrowRight size={18} className="gold-text opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </h3>
              <p className="text-zinc-400 font-light text-sm leading-relaxed">{r.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA + INTERNAL LINKS */}
      <section className="bg-black/40 backdrop-blur-md border-y border-white/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-white">Reserve Your Aspen Transportation</h2>
          <p className="mt-4 text-zinc-300 font-light">Request a quote in minutes, or call/text us directly — available 24/7.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/reserve" data-testid="aspen-cta-reserve" className={CTA_BTN}>Reserve Your Ride <ArrowRight size={16} /></Link>
            <a href={`tel:${PHONE}`} data-testid="aspen-cta-call" className={CTA_BTN}><Phone size={16} /> Call or Text Now</a>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm">
            <Link to="/fleet" data-testid="aspen-link-fleet" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">View Our Fleet <ArrowRight size={14} /></Link>
            <Link to="/rates" data-testid="aspen-link-rates" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">See Our Rates <ArrowRight size={14} /></Link>
            <Link to="/airport-transportation" data-testid="aspen-link-airport" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">Airport Transportation <ArrowRight size={14} /></Link>
            <Link to="/reserve" data-testid="aspen-link-quote" className="inline-flex items-center gap-1.5 gold-text hover:gap-2.5 transition-all">Request a Quote <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
